import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { Email, GeneralStatus, SendGridContext } from 'types/emailService';
import { Domain } from 'types/domainServiceV2';
import { getAllDomains } from 'api/operations/domainServiceV2';
import { domainServiceHelper } from 'global/services-helpers';

export default class EmailsDashboardStore {
  rootStore: RootStore;
  loading = false;
  modalLoading = false;

  emails: Email[] = [];
  domains: Domain[] = [];
  emailTypes: string[] = [];
  // Types to show in the search email types select
  filteredEmailTypes: string[] = [];
  totalElements = 0;
  totalPages = 0;
  page = 1;
  sponsorId = '';
  emailType = '';
  searchEmail = '';
  emailStatus: GeneralStatus | null = null;
  date = {
    from: new Date().toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  };

  isModalOpen = false;
  selectedEmail: Email | null = null;
  selectedSendGridContext: SendGridContext | null = null;

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setModalLoading(loadingStatus: boolean): void {
    this.modalLoading = loadingStatus;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedEmail = null;
  }

  public async openModal(email: Email): Promise<void> {
    this.isModalOpen = true;
    this.setModalLoading(true);
    const context = await this.rootStore.emailStore.getSendGridContext(email.id);
    runInAction(() => {
      this.selectedEmail = email;
      this.selectedSendGridContext = context;
      this.setModalLoading(false);
    });
  }

  public setSponsorId(sponsorId: string): void {
    this.sponsorId = sponsorId;
  }

  public setEmailType(type: string): void {
    this.emailType = type;
  }

  public setEmailStatus(status: GeneralStatus | null): void {
    this.emailStatus = status;
  }

  public setPage(page: number): void {
    this.page = page;
  }

  public setSearchEmailAddress(searchEmail: string): void {
    this.searchEmail = searchEmail;
  }

  public updateDate(from: string, to: string): void {
    this.date = { from, to };
  }

  public async getAllDomains(): Promise<void> {
    const domains = await getAllDomains();
    runInAction(() => {
      this.domains = domains;
    });
  }

  public async getEmailTypes(): Promise<void> {
    const res = await this.rootStore.emailStore.getEmailTypes();
    runInAction(() => {
      this.emailTypes = res;
    });
  }

  public async getInitialData(): Promise<void> {
    this.setLoading(true);
    await Promise.all([this.getEmails(), this.getAllDomains(), this.getEmailTypes()]);
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public async getEmails(): Promise<void> {
    this.setLoading(true);
    const result = await this.rootStore.emailStore.getEmailsByRange(
      this.date.from,
      this.date.to,
      this.sponsorId,
      this.emailType,
      this.emailStatus || null,
      this.page,
      this.searchEmail,
    );
    runInAction(() => {
      this.totalElements = result.totalElements;
      this.totalPages = result.totalPages;
      this.emails = result.result;
      this.setLoading(false);
    });
  }

  // Methods for search by email type
  public setFilteredEmailTypes(types: string[]): void {
    this.filteredEmailTypes = types;
  }

  // Function to normalize email types for easy comparison
  // eslint-disable-next-line class-methods-use-this
  private normalizeSearchValue(str: string): string {
    return str.toLowerCase().replace(/[_]/g, ' ').trim();
  }

  // eslint-disable-next-line class-methods-use-this
  public searchTypes(searchValue: string): void {
    const convertedEmailTypes = this.emailTypes.map((type) => toJS(type)).slice();
    const normalizedSearchValue = this.normalizeSearchValue(searchValue);

    // Filter the list based on the normalized search value
    const filteredList = convertedEmailTypes.filter((type) => {
      return this.normalizeSearchValue(type).includes(normalizedSearchValue);
    });
    this.setFilteredEmailTypes(filteredList);
  }

  // Filter Options
  /**
   * Sponsor Options with parameter 'all' that allows to get data for all sponsors
   */
  public getSponsorOptionsWithOptionAll(): OptionType[] {
    const convertedDomains = this.domains.map((domain) => toJS(domain)).slice();
    return [{ value: '', label: 'All' }, ...domainServiceHelper.getSponsorOptions(convertedDomains)];
  }

  // eslint-disable-next-line class-methods-use-this
  public getEmailTypeOptions(): OptionType[] {
    const convertedEmailTypes = this.emailTypes.map((type) => toJS(type)).slice();
    const emailTypeOptions = convertedEmailTypes.map((type) => ({
      value: type,
      label: capitalize(type.split('_').join(' ').toLowerCase()),
    }));
    return [{ value: '', label: 'All' }, ...emailTypeOptions];
  }

  // eslint-disable-next-line class-methods-use-this
  public getEmailStatusOptions(): OptionType[] {
    const emailStatusOptions = Object.keys(GeneralStatus).map((status) => ({
      value: status,
      label: capitalize(status.replaceAll('_', ' ').toLowerCase()),
    }));
    return [{ value: '', label: 'All' }, ...emailStatusOptions];
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
